/**
 * The session runner: one block per screen, footer action advances.
 *
 * Every answer is written immediately, both locally and to the server when
 * online. Closing the app mid-session loses nothing (docs/01).
 */

import { api, ApiError } from "../api.ts";
import { prefetch, stop as stopAudio } from "../audio.ts";
import { renderConversation } from "../blocks/conversation.ts";
import { renderDictation } from "../blocks/dictation.ts";
import { renderExplanation } from "../blocks/explanation.ts";
import { renderFlashcard } from "../blocks/flashcard.ts";
import { renderProduction } from "../blocks/production.ts";
import type { BlockContext, BlockResult, DeferredAnswer, RenderedBlock } from "../blocks/types.ts";
import { cacheSession, cachedSession, queueAttempt } from "../store.ts";
import type { DictationPayload, SessionView } from "../types.ts";
import { el, mount, primaryButton, shell } from "../ui.ts";

export interface SessionHandlers {
	complete: (sessionId: number, deferred: DeferredAnswer[]) => void;
	exit: () => void;
}

const KIND_LABEL: Record<string, string> = {
	explanation: "Explicação",
	conversation: "Conversa",
	flashcard: "Vocabulário",
	dictation: "Ditado",
	production: "Produção",
};

export async function runSession(sessionId: number, handlers: SessionHandlers, online: boolean): Promise<void> {
	let session: SessionView;
	try {
		session = await api.session(sessionId);
		// Local progress can legitimately be ahead of the server's: an explanation
		// block finishes without producing an attempt, and the completion POST is
		// fire-and-forget. Without this merge a refresh drops back to block 0 and
		// the lesson starts over.
		await mergeCachedProgress(session);
		await cacheSession(session);
		void api.start(sessionId).catch(() => {});
	} catch (error) {
		// Network-first with cache fallback: a session already fetched is fully
		// usable offline.
		const cached = await cachedSession(sessionId);
		if (cached === undefined) {
			mount(
				shell({
					eyebrowLeft: "Fala",
					body: [
						el("div", {
							class: "notice notice--brick",
							text:
								error instanceof ApiError
									? error.message
									: "This session is not on the device and there is no connection.",
						}),
					],
					footer: primaryButton("Back to today", handlers.exit, { variant: "ghost" }),
				}),
			);
			return;
		}
		session = cached;
	}

	// Prefetch every clip on open so a dictation block never waits on the
	// network (docs/04).
	void prefetch(
		session.blocks
			.filter((block) => block.kind === "dictation")
			.map((block) => (block.payload as unknown as DictationPayload).audio_url),
	);

	const isTest = session.kind === "test";
	const deferred: DeferredAnswer[] = [];
	// Resume where the learner left off. findIndex returns -1 when every block
	// is done, which means the session is finished, not that it starts again.
	let index = session.blocks.findIndex((block) => !block.completed);
	if (index === -1) index = session.blocks.length;

	const step = (): void => {
		stopAudio();
		if (index >= session.blocks.length) {
			handlers.complete(session.id, deferred);
			return;
		}
		renderBlock();
	};

	function renderBlock(): void {
		const block = session.blocks[index];
		if (block === undefined) {
			handlers.complete(session.id, deferred);
			return;
		}

		const footerHolder = el("footer", { class: "foot" });

		const context: BlockContext = {
			session,
			block,
			englishRatio: session.english_ratio,
			isTest,
			online,
			finish: (result: BlockResult) => {
				for (const attempt of result.attempts) void queueAttempt(attempt);
				if (result.deferred !== undefined) deferred.push(result.deferred);
				block.completed = true;
				void cacheSession(session);
				// Tells the server about blocks that produce no attempt — an
				// explanation, or a conversation the learner skipped. Fire and
				// forget: the cached flag above is what makes a refresh resume
				// correctly if this never lands.
				void api.completeBlocks(session.id, [block.id]).catch(() => {});
				index += 1;
				step();
			},
			setFooter: (node: Node | null) => {
				footerHolder.replaceChildren();
				if (node !== null) footerHolder.append(node);
			},
		};

		const rendered = renderFor(context);
		if (rendered.footer !== null) footerHolder.append(rendered.footer);

		const evaluationBlocks = session.blocks.filter((candidate) => candidate.kind !== "explanation").length;
		const doneEvaluation = session.blocks
			.slice(0, index)
			.filter((candidate) => candidate.kind !== "explanation").length;

		const fragment = shell({
			// Answers are written as they are given, so leaving mid-session costs
			// nothing and reopening returns to this block.
			back: { label: "Hoje", onClick: handlers.exit },
			eyebrowLeft: isTest
				? `Teste · sem correções`
				: `${KIND_LABEL[block.kind] ?? block.kind} · ${index + 1} / ${session.blocks.length}`,
			eyebrowRight: isTest ? undefined : blockCounter(session, index),
			progress: { done: index, total: session.blocks.length },
			meta: isTest
				? `Pergunta ${Math.min(doneEvaluation + 1, evaluationBlocks)} de ${evaluationBlocks}`
				: undefined,
			testChrome: isTest,
			body: rendered.body,
		});

		fragment.append(footerHolder);
		mount(fragment);
	}

	step();
}

/**
 * Completion is a one-way flag, so the union of the two copies is the truth.
 * The server is authoritative for everything else in the session — only the
 * flag is carried over.
 */
async function mergeCachedProgress(session: SessionView): Promise<void> {
	const cached = await cachedSession(session.id);
	if (cached === undefined) return;

	const done = new Set(cached.blocks.filter((block) => block.completed).map((block) => block.id));
	const missed: number[] = [];
	for (const block of session.blocks) {
		if (block.completed || !done.has(block.id)) continue;
		block.completed = true;
		missed.push(block.id);
	}

	// The server did not know about these, so tell it now rather than leaving
	// the gap to be rediscovered on the next open.
	if (missed.length > 0) void api.completeBlocks(session.id, missed).catch(() => {});
}

function renderFor(context: BlockContext): RenderedBlock {
	switch (context.block.kind) {
		case "explanation":
			return renderExplanation(context);
		case "conversation":
			return renderConversation(context);
		case "flashcard":
			return renderFlashcard(context);
		case "dictation":
			return renderDictation(context);
		case "production":
			return renderProduction(context);
		default:
			return {
				body: [el("div", { class: "notice", text: `Unknown block type "${String(context.block.kind)}".` })],
				footer: primaryButton("Skip", () => context.finish({ attempts: [] })),
			};
	}
}

function blockCounter(session: SessionView, index: number): string | undefined {
	const block = session.blocks[index];
	if (block === undefined) return undefined;
	if (block.kind === "flashcard") {
		const cards = session.blocks.filter((candidate) => candidate.kind === "flashcard");
		const position = cards.findIndex((candidate) => candidate.id === block.id) + 1;
		return `${position} de ${cards.length}`;
	}
	if (block.kind === "dictation") {
		const clips = session.blocks.filter((candidate) => candidate.kind === "dictation");
		const position = clips.findIndex((candidate) => candidate.id === block.id) + 1;
		const payload = block.payload as unknown as DictationPayload;
		return payload.voice === "" ? `clipe ${position} de ${clips.length}` : payload.voice;
	}
	return undefined;
}
